import { headerToKey, indexToLetter } from './keys';
import { DEFAULT_BRANDING, sanitizeColor, sanitizeLogoUrl } from './branding';
import { syncPortalFields } from './portal_fields';

/**
 * @typedef {'header'|'letter'|'number'} ColumnReference
 */

/**
 * @typedef {Object} PortalColumn
 * @property {string} header
 * @property {string} key
 * @property {ColumnReference} reference
 * @property {string} letter
 * @property {number} number
 */

/**
 * @typedef {Object} PortalConfig
 * @property {number} version
 * @property {string} templateKey
 * @property {{ name: string, columns: PortalColumn[], defaultReference: ColumnReference, lookup: object, urlParam: string }} sheet
 * @property {{ htmlFileName: string, pageTitle: string, webAppUrlPlaceholder: string, renewalDays: number, allowAllFrames: boolean }} script
 * @property {{ multiRowLookup: boolean, doPostRenew: boolean }} features
 * @property {{ sections: object }} html
 */

export function createBlankConfig() {
  return normalizeConfig({
    version: 1,
    templateKey: 'blank',
    sheet: {
      name: 'Sheet1',
      columns: [],
      defaultReference: 'header',
      lookup: { reference: 'header', header: '', letter: 'A', number: 1 },
      urlParam: 'id'
    },
    script: {
      htmlFileName: 'Page',
      pageTitle: 'My Portal',
      webAppUrlPlaceholder: 'PASTE_DEPLOYMENT_URL_HERE',
      renewalDays: 14,
      allowAllFrames: true
    },
    features: { multiRowLookup: false, doPostRenew: false },
    html: {
      sections: {
        search: { enabled: true, label: 'ID', placeholder: '', buttonText: 'Search' },
        intro: { enabled: false, title: '', subtitle: '', body: '' },
        rowCards: [],
        loansList: { enabled: false },
        renewForm: { enabled: false, renewalDays: 14, statusValues: ['Checked Out'] },
        notFound: { message: 'Not found.' },
        branding: { enabled: false, ...DEFAULT_BRANDING },
        portalFields: []
      }
    }
  });
}

/**
 * @param {string[]} headers
 * @param {ColumnReference} [reference]
 * @returns {PortalColumn[]}
 */
export function columnsFromHeaders(headers, reference = 'header') {
  return headers.map((header, i) => ({
    header,
    key: headerToKey(header),
    reference,
    letter: indexToLetter(i),
    number: i + 1
  }));
}

/**
 * @param {PortalConfig} config
 * @returns {PortalConfig}
 */
export function normalizeConfig(config) {
  const c = structuredClone(config);
  c.version = 1;
  c.sheet = c.sheet || {};
  c.sheet.name = c.sheet.name || 'Sheet1';
  c.sheet.defaultReference =
    c.sheet.defaultReference === 'letter' ? 'letter' : 'header';
  c.sheet.urlParam = (c.sheet.urlParam || 'id').replace(/[^\w]/g, '') || 'id';
  c.sheet.lookup = c.sheet.lookup || {
    reference: 'header',
    header: '',
    letter: 'A',
    number: 1,
    columnIndex: -1
  };
  if (typeof c.sheet.lookup.columnIndex !== 'number') {
    c.sheet.lookup.columnIndex = -1;
  }
  c.sheet.columns = (c.sheet.columns || []).map((col, i) => ({
    header: col.header || '',
    key: col.key || headerToKey(col.header),
    reference: col.reference || c.sheet.defaultReference || 'header',
    letter: col.letter || indexToLetter(i),
    number: col.number ?? i + 1
  }));
  c.script = {
    htmlFileName: 'Page',
    pageTitle: c.script?.pageTitle || 'My Portal',
    webAppUrlPlaceholder: c.script?.webAppUrlPlaceholder || 'PASTE_DEPLOYMENT_URL_HERE',
    renewalDays: c.script?.renewalDays ?? 14,
    allowAllFrames: c.script?.allowAllFrames !== false
  };
  c.features = {
    multiRowLookup: Boolean(c.features?.multiRowLookup),
    doPostRenew: Boolean(c.features?.doPostRenew),
    shipmentTracker: Boolean(c.features?.shipmentTracker)
  };
  c.html = c.html || { sections: {} };
  c.html.sections = c.html.sections || {};
  const b = { ...DEFAULT_BRANDING, ...(c.html.sections.branding || {}) };
  const logoUrl = sanitizeLogoUrl(b.logoUrl);
  const companyName = String(b.companyName || '').trim();
  const description = String(b.description || '').trim();
  const enabled =
    typeof b.enabled === 'boolean' ? b.enabled : Boolean(logoUrl || companyName || description);
  c.html.sections.branding = {
    enabled,
    primaryColor: sanitizeColor(b.primaryColor, DEFAULT_BRANDING.primaryColor),
    backgroundColor: sanitizeColor(b.backgroundColor, DEFAULT_BRANDING.backgroundColor),
    cardColor: sanitizeColor(b.cardColor, DEFAULT_BRANDING.cardColor),
    textColor: sanitizeColor(b.textColor, DEFAULT_BRANDING.textColor),
    description,
    logoUrl,
    companyName
  };
  if (!Array.isArray(c.html.sections.portalFields)) {
    c.html.sections.portalFields = [];
  }
  if (c.sheet.columns?.length) syncPortalFields(c);
  return c;
}

/**
 * @param {PortalConfig} config
 * @param {string} header
 */
export function ensureColumn(config, header) {
  const h = String(header || '').trim();
  if (!h) return;
  const exists = config.sheet.columns.some((c) => c.header === h);
  if (exists) return;
  const i = config.sheet.columns.length;
  config.sheet.columns.push({
    header: h,
    key: headerToKey(h),
    reference: config.sheet.defaultReference || 'header',
    letter: indexToLetter(i),
    number: i + 1
  });
}
